import * as React from "react";
import { formatUzPhone } from "../phone";
import {
  BarChart3,
  Bell,
  ClipboardList,
  Cog,
  Home,
  LogOut,
  MessageSquare,
  Package,
  Send,
  Users,
  Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { BrandWordmark } from "./brand";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { useTariff } from "../lib/use-tariff";
import { hiddenTabs } from "../lib/plan-visibility";
import type { UserOut } from "../api";
import type { Tab } from "../routing";
import { useFinance } from "../lib/use-finance";

interface SidebarProps {
  tab: Tab;
  setTab: (t: Tab) => void;
  user: UserOut;
  onLogout: () => void;
  onClose?: () => void;
  // Faqat desktop uchun: ikonka-rail rejimi. Mobil drawer buni uzatmaydi —
  // u har doim to'liq (yozuvli) holatda ochiladi.
  collapsed?: boolean;
}

interface NavItem {
  id: Tab;
  // common.json → tabs.<id>.title orqali resolve qilinadi.
  icon: React.ReactNode;
  feature: string;
  // Gated bo'lsa ko'rsatiladigan upsell badge ("Pro" / "Max").
  badge?: string;
}

interface NavGroup {
  // common.json → nav.groups.<titleKey> orqali resolve qilinadi.
  titleKey: string;
  items: NavItem[];
}

// Guruhlangan IA — tekis ro'yxat o'rniga ma'noli bo'limlar.
const GROUPS: NavGroup[] = [
  {
    titleKey: "nav.groups.daily",
    items: [
      { id: "home", icon: <Home />, feature: "home" },
      { id: "works", icon: <ClipboardList />, feature: "works" },
      { id: "clients", icon: <Users />, feature: "clients" },
      // Broadcast ALOHIDA bayroq: standard'da transactional (manual_messaging)
      // bor, broadcast esa yo'q — ikkalasini aralashtirib bo'lmaydi.
      { id: "broadcast", icon: <Send />, feature: "broadcast" },
      { id: "reminders", icon: <Bell />, feature: "reminders" },
    ],
  },
  {
    titleKey: "nav.groups.warehouseFinance",
    items: [
      { id: "qarzlar", icon: <Wallet />, feature: "qarzlar", badge: "Comfort" },
      { id: "kassa", icon: <Wallet />, feature: "kassa" },
      { id: "foyda_zarar", icon: <Wallet />, feature: "foyda_zarar" },
      { id: "ish_haqi", icon: <Wallet />, feature: "ish_haqi" },
      { id: "xarajatlar", icon: <Wallet />, feature: "xarajatlar" },
      // Qaytarishlar/Jarayonda alohida band emas — Ombor sahifasining tablari.
      { id: "tovars", icon: <Package />, feature: "tovars", badge: "Pro" },
    ],
  },
  {
    titleKey: "nav.groups.teamAnalytics",
    items: [
      { id: "employees", icon: <Users />, feature: "employees", badge: "Pro" },
      { id: "analytics", icon: <BarChart3 />, feature: "analytics", badge: "Pro" },
      { id: "murojaatlar", icon: <MessageSquare />, feature: "murojaatlar", badge: "Pro" },
    ],
  },
];

export function Sidebar({
  tab,
  setTab,
  user,
  onLogout,
  onClose,
  collapsed = false,
}: SidebarProps) {
  const { t } = useTranslation("common");
  const { status, has } = useTariff();
  const tariff = status?.tariff ?? "trial";
  const tariffLabel = t(`tariff.labels.${tariff}`);
  // Obuna muddati tugaganda: Sozlamalardan tashqari hamma navigatsiya bloklanadi
  // (egasi aktivatsiya kodini kiritishi uchun Sozlamalar ochiq qoladi).
  const expired = !!(status?.is_expired || status?.status === "expired");
  const finance = useFinance();
  const hidden = new Set(hiddenTabs(status?.features));
  if (!finance.active("kassa")) hidden.add("kassa");
  if (!finance.active("foyda_zarar")) hidden.add("foyda_zarar");
  if (!finance.active("ish_haqi")) hidden.add("ish_haqi");
  if (!finance.active("xarajatlar")) hidden.add("xarajatlar");

  const handleClick = (t: Tab) => {
    setTab(t);
    onClose?.();
  };

  return (
    <aside
      className={cn(
        "dashboard-sidebar flex flex-col h-full border-r bg-card text-card-foreground",
        "transition-[width] duration-200",
        collapsed ? "w-16" : "w-64",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2.5 py-4 border-b",
          collapsed ? "justify-center px-0" : "px-5",
        )}
      >
        <div className="size-9 rounded-[8px] bg-[#0A0E18] p-[3px] shrink-0">
          <img
            src="/logo-mark.png"
            alt="AvtoDaftar"
            className="w-full h-full object-cover rounded-[5px]"
          />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="font-num text-base font-bold tracking-tight">
              <BrandWordmark />
            </span>
            <span className="text-[11px] text-muted-foreground">
              {t("brand.tagline")}
            </span>
          </div>
        )}
      </div>

      <nav
        className={cn(
          "flex-1 overflow-y-auto space-y-4 scrollbar-thin",
          collapsed ? "p-2" : "p-3",
        )}
      >
        {GROUPS.map((group) => {
          // Tarifda ataylab yo'q bo'lgan bo'limlar butunlay olib tashlanadi
          // (upsell holatida ham qolmaydi) — qarang: lib/plan-visibility.ts.
          const items = group.items.filter((item) => !hidden.has(item.id));
          // Guruh butunlay bo'shab qolsa sarlavhasi ham chiqmaydi.
          if (items.length === 0) return null;
          return (
            <div key={group.titleKey} className="space-y-0.5">
              {/* Yig'ilgan holatda sarlavha sig'maydi — guruhlar orasidagi
                  `space-y-4` bo'shliq ajratib turadi. */}
              {!collapsed && (
                <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {t(group.titleKey)}
                </div>
              )}
              {items.map((item) => (
                <NavButton
                  key={item.id}
                  item={item}
                  label={t(`tabs.${item.id}.title`)}
                  enabled={has(item.feature)}
                  locked={expired}
                  active={tab === item.id}
                  collapsed={collapsed}
                  onClick={() => handleClick(item.id)}
                />
              ))}
            </div>
          );
        })}

        <div className="space-y-0.5 pt-1">
          <NavButton
            item={{
              id: "settings",
              icon: <Cog />,
              feature: "settings",
            }}
            label={t("nav.settings")}
            enabled={has("settings")}
            active={tab === "settings" || tab === "tariff"}
            collapsed={collapsed}
            onClick={() => handleClick("settings")}
          />
        </div>
      </nav>

      <Separator />

      <div className={cn("space-y-3", collapsed ? "p-2" : "p-3")}>
        <button
          type="button"
          className={cn(
            "flex w-full items-center gap-2 rounded-md bg-muted/60 py-2 text-left hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            collapsed ? "justify-center px-1" : "justify-between px-3",
          )}
          onClick={() => handleClick("tariff")}
          title={collapsed ? `${t("tariff.title")}: ${tariffLabel}` : undefined}
          aria-label={`${t("tariff.title")}: ${tariffLabel}`}
        >
          {!collapsed && (
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t("tariff.title")}
              </span>
              <span className="font-num font-semibold text-sm">{tariffLabel}</span>
            </div>
          )}
          {/* Chip tarif NOMINI ko'rsatadi, lekin uslubi tarifga bog'lanmaydi —
              frontendda tarif nomi bo'yicha shartlar taqiqlangan (gate'lar
              faqat bayroqlar orqali). */}
          {/* Yig'ilganda to'liq nom sig'maydi — bosh harf, to'lig'i `title`da. */}
          <Badge variant="secondary" className="font-num">
            {collapsed ? tariffLabel.slice(0, 1).toUpperCase() : tariffLabel}
          </Badge>
        </button>

        <div
          className={cn(
            "flex items-center gap-3",
            collapsed ? "justify-center px-0" : "px-1",
          )}
          title={collapsed ? `${user.full_name} · ${formatUzPhone(user.phone)}` : undefined}
        >
          <div className="size-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-num font-bold text-xs shrink-0">
            {user.full_name.slice(0, 2).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">{user.full_name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {formatUzPhone(user.phone)}
              </div>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          className={cn(
            "w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10",
            collapsed ? "justify-center px-0" : "justify-start",
          )}
          onClick={onLogout}
          title={collapsed ? t("nav.logout") : undefined}
          aria-label={collapsed ? t("nav.logout") : undefined}
        >
          <LogOut />
          {!collapsed && t("nav.logout")}
        </Button>
      </div>
    </aside>
  );
}

function NavButton({
  item,
  label,
  enabled,
  active,
  onClick,
  locked = false,
  collapsed = false,
}: {
  item: NavItem;
  label: string;
  enabled: boolean;
  active: boolean;
  onClick: () => void;
  // Obuna tugagani uchun bloklangan — bosib bo'lmaydi (Pro-gating'dan farqli,
  // u upsell uchun bosiladigan bo'lib qoladi).
  locked?: boolean;
  collapsed?: boolean;
}) {
  const { t } = useTranslation("common");
  const gatedHint = !enabled ? t("nav.gatedTooltip") : undefined;
  // Yig'ilgan holatda yozuv yo'q — nom native `title` orqali beriladi
  // (loyihada hover tooltip primitivi yo'q, qarang: components/info-tooltip.tsx).
  const title = collapsed
    ? gatedHint
      ? `${label} · ${gatedHint}`
      : label
    : gatedHint;
  return (
    <button
      onClick={onClick}
      disabled={locked}
      className={cn(
        "sidebar-nav-item relative w-full flex items-center rounded-md py-2 text-[13.5px] transition-colors text-left",
        collapsed ? "justify-center px-0" : "gap-3 px-3",
        "[&_svg]:size-[17px] [&_svg]:shrink-0 [&_svg]:opacity-90",
        active
          ? "is-active bg-accent text-foreground font-semibold"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
        !enabled && "opacity-60",
        locked && "opacity-50 pointer-events-none",
      )}
      title={title}
      aria-label={collapsed ? label : undefined}
    >
      {active && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-primary" />
      )}
      {item.icon}
      {!collapsed && <span className="flex-1">{label}</span>}
      {!enabled &&
        !locked &&
        // Yig'ilganda badge sig'maydi — upsell holati kichik nuqta bilan
        // belgilanadi, to'liq matn `title`da.
        (collapsed ? (
          <span className="absolute right-1 top-1 size-1.5 rounded-full bg-muted-foreground/60" />
        ) : (
          <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
            {item.badge ?? "Pro"}
          </Badge>
        ))}
    </button>
  );
}
